import React, { useEffect, useMemo, useState } from "react"
import { Outlet, useLocation } from "react-router-dom"
import { useAuth } from "../context/AuthContext"
import Sidebar from "./Sidebar"
import { ClinicProvider, useActiveClinic } from "../context/ClinicContext"
import { useClinicSubscription } from "../features/subscriptions/hooks/useClinicSubscription"
import OnboardingWelcomeCard from "../features/onboarding/components/OnboardingWelcomeCard"
import { ui } from "../shared/styles/uiSystem"
import OdontoFlowLogo from "../shared/brand/OdontoFlowLogo"

const MODULE_META = [
  {
    match: (pathname) => pathname.startsWith("/dashboard"),
    group: "Operación",
    title: "Dashboard",
  },
  {
    match: (pathname) => pathname.startsWith("/agenda"),
    group: "Operación",
    title: "Agenda",
  },
  {
    match: (pathname) => pathname.startsWith("/patients"),
    group: "Pacientes",
    title: "Pacientes",
  },
  {
    match: (pathname) => pathname.startsWith("/clinical-history"),
    group: "Clínica",
    title: "Historia clínica",
  },
  {
    match: (pathname) => pathname.startsWith("/treatment-plans"),
    group: "Clínica",
    title: "Planes",
  },
  {
    match: (pathname) => pathname.startsWith("/billing"),
    group: "Finanzas",
    title: "Cobros",
  },
  {
    match: (pathname) => pathname.startsWith("/cash-closing"),
    group: "Finanzas",
    title: "Caja diaria",
  },
  {
    match: (pathname) => pathname.startsWith("/financial-executive"),
    group: "Finanzas",
    title: "Finanzas ejecutivas",
  },
  {
    match: (pathname) => pathname.startsWith("/ia"),
    group: "Inteligencia",
    title: "Centro IA",
  },
  {
    match: (pathname) => pathname === "/settings",
    group: "Administración",
    title: "Centro de configuración",
  },
  {
    match: (pathname) => pathname.startsWith("/settings/clinic-resources"),
    group: "Administración",
    title: "Sedes y recursos",
  },
  {
    match: (pathname) => pathname.startsWith("/settings/team-access"),
    group: "Administración",
    title: "Equipo y accesos",
  },
  {
    match: (pathname) => pathname.startsWith("/settings/schedule-availability"),
    group: "Administración",
    title: "Agenda y disponibilidad",
  },
  {
    match: (pathname) => pathname.startsWith("/settings/treatments-permissions"),
    group: "Administración",
    title: "Tratamientos y permisos",
  },
  {
    match: (pathname) => pathname.startsWith("/settings/clinical-engine"),
    group: "Administración",
    title: "Motor clínico",
  },
  {
    match: (pathname) => pathname.startsWith("/settings/system-account"),
    group: "Administración",
    title: "Sistema y cuenta",
  },
  {
    match: (pathname) => pathname.startsWith("/settings"),
    group: "Administración",
    title: "Configuración",
  },
]

function resolveModuleMeta(pathname) {
  return (
    MODULE_META.find((item) => item.match(pathname)) || {
      group: "OdontoFlow",
      title: "Plataforma",
    }
  )
}

function AppShell() {
  const location = useLocation()

  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1440,
  )
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [installPrompt, setInstallPrompt] = useState(null)
  const [installDismissed, setInstallDismissed] = useState(() => {
    if (typeof window === "undefined") return false
    return window.localStorage.getItem("odontoflow_install_dismissed") === "1"
  })
  const [isStandalone, setIsStandalone] = useState(() => {
    if (typeof window === "undefined") return false

    return (
      window.matchMedia?.("(display-mode: standalone)")?.matches ||
      window.navigator?.standalone === true
    )
  })

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth)
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [])

  const isTablet = viewportWidth < 1200
  const isMobile = viewportWidth < 900
  const isVerySmallMobile = viewportWidth < 520

  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!isMobile) {
      setMobileMenuOpen(false)
    }
  }, [isMobile])

  useEffect(() => {
    if (typeof document === "undefined") return undefined

    if (mobileMenuOpen) {
      const previousOverflow = document.body.style.overflow
      document.body.style.overflow = "hidden"

      return () => {
        document.body.style.overflow = previousOverflow
      }
    }

    return undefined
  }, [mobileMenuOpen])

    useEffect(() => {
    if (typeof window === "undefined") return undefined

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault()
      setInstallPrompt(event)
    }

    const handleInstalled = () => {
      setInstallPrompt(null)
      setIsStandalone(true)
      window.localStorage.removeItem("odontoflow_install_dismissed")
    }

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt)
    window.addEventListener("appinstalled", handleInstalled)

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt)
      window.removeEventListener("appinstalled", handleInstalled)
    }
  }, [])

  const handleInstallApp = async () => {
    if (!installPrompt) return

    installPrompt.prompt()

    try {
      const result = await installPrompt.userChoice

      if (result?.outcome === "accepted") {
        setIsStandalone(true)
      }
    } finally {
      setInstallPrompt(null)
    }
  }

  const handleDismissInstall = () => {
    setInstallDismissed(true)

    if (typeof window !== "undefined") {
      window.localStorage.setItem("odontoflow_install_dismissed", "1")
    }
  }

  const {
    activeClinicId,
    clinicOptions,
    loadingClinics,
    setActiveClinicId,
  } = useActiveClinic()

  const { user, logout } = useAuth()

  const storedClinicId =
    typeof window !== "undefined"
      ? window.localStorage.getItem("odontoflow_clinic_id")
      : null

  const realClinicOptions = useMemo(
    () => clinicOptions.filter((clinic) => clinic.id !== "ALL"),
    [clinicOptions],
  )

  const hasMultipleClinics = realClinicOptions.length > 1

  const activeClinicName = useMemo(() => {
    if (activeClinicId === "ALL") return "Todas las clínicas"

    const active = clinicOptions.find((clinic) => clinic.id === activeClinicId)
    if (active?.name) return active.name

    if (realClinicOptions.length === 1) return realClinicOptions[0].name

    return loadingClinics ? "Cargando clínicas" : "Selecciona clínica"
  }, [activeClinicId, clinicOptions, realClinicOptions, loadingClinics])

  const subscriptionClinicId =
    activeClinicId && activeClinicId !== "ALL"
      ? activeClinicId
      : activeClinicId === "ALL" && realClinicOptions[0]?.id
        ? realClinicOptions[0].id
        : !activeClinicId && storedClinicId && storedClinicId !== "ALL"
          ? storedClinicId
          : realClinicOptions[0]?.id || null

  const {
    subscription,
    loading: loadingSubscription,
    error: subscriptionError,
  } = useClinicSubscription(subscriptionClinicId)

  const moduleMeta = useMemo(
    () => resolveModuleMeta(location.pathname),
    [location.pathname],
  )

  const isAgendaContext = location.pathname.startsWith("/agenda")

  const subscriptionLabel = (() => {
    if (!subscriptionClinicId) return "Sin clínica"
    if (loadingSubscription) return "Cargando plan"
    if (subscriptionError) return "Plan no disponible"
    if (!subscription) return "Plan activo"

    const planName =
      subscription.planName ||
      subscription.plan?.name ||
      subscription.planCode ||
      "Plan activo"

    return `Plan ${String(planName).toUpperCase()}`
  })()

  const subscriptionMeta = (() => {
    if (!subscriptionClinicId || loadingSubscription || subscriptionError || !subscription) {
      return ""
    }

    const daysLeft =
      subscription.daysRemaining ??
      subscription.remainingDays ??
      subscription.trialDaysRemaining ??
      null

    if (daysLeft === null || daysLeft === undefined || Number.isNaN(Number(daysLeft))) {
      return subscription.status ? `Estado: ${subscription.status}` : ""
    }

    if (Number(daysLeft) === 1) return "1 día restante"
    return `${daysLeft} días restantes`
  })()

  const subscriptionTone = (() => {
    if (subscriptionError) {
      return {
        bg: "#fff1f2",
        border: "#fecdd3",
        color: "#be123c",
      }
    }

    if (!subscription || loadingSubscription) {
      return {
        bg: "#eff6ff",
        border: "#bfdbfe",
        color: "#1d4ed8",
      }
    }

    const daysLeft =
      subscription.daysRemaining ??
      subscription.remainingDays ??
      subscription.trialDaysRemaining ??
      null

    if (daysLeft !== null && Number(daysLeft) <= 5) {
      return {
        bg: "#fff7ed",
        border: "#fed7aa",
        color: "#c2410c",
      }
    }

    return {
      bg: "#f0fdf4",
      border: "#bbf7d0",
      color: "#166534",
    }
  })()

  const showOnboardingCard = !activeClinicId || activeClinicId === "ALL"

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "268px minmax(0, 1fr)",
        minHeight: "100vh",
        background: "#f4f7fb",
      }}
    >
      {!isMobile && (
        <Sidebar
          user={user}
          logout={logout}
          subscriptionLabel={subscriptionLabel}
          subscriptionMeta={subscriptionMeta}
          subscriptionTone={subscriptionTone}
        />
      )}

      {isMobile ? (
        <MobileNavigationDrawer
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          user={user}
          logout={logout}
          subscriptionLabel={subscriptionLabel}
          subscriptionMeta={subscriptionMeta}
          subscriptionTone={subscriptionTone}
        />
      ) : null}

      <main
        style={{
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          background: "linear-gradient(180deg, #f4f7fb 0%, #eef3f9 100%)",
        }}
      >
        <div
          style={{
            minHeight: isMobile ? (isAgendaContext ? 42 : 50) : 58,
            background: "rgba(255,255,255,0.94)",
            borderBottom: "1px solid #e2e8f0",
            backdropFilter: "blur(12px)",
            position: "sticky",
            top: 0,
            zIndex: 30,
            padding: isMobile ? (isAgendaContext ? "5px 7px" : "6px 8px") : "8px 14px",
          }}
        >
          {isMobile ? (
            <MobileTopBar
              user={user}
              logout={logout}
              moduleMeta={moduleMeta}
              activeClinicId={activeClinicId}
              clinicOptions={clinicOptions}
              loadingClinics={loadingClinics}
              setActiveClinicId={setActiveClinicId}
              activeClinicName={activeClinicName}
              hasMultipleClinics={hasMultipleClinics}
              onOpenMenu={() => setMobileMenuOpen(true)}
              isVerySmallMobile={isVerySmallMobile}
              isAgendaContext={isAgendaContext}
            />
          ) : (
            <DesktopTopBar
              user={user}
              logout={logout}
              moduleMeta={moduleMeta}
              activeClinicId={activeClinicId}
              clinicOptions={clinicOptions}
              loadingClinics={loadingClinics}
              setActiveClinicId={setActiveClinicId}
              activeClinicName={activeClinicName}
              hasMultipleClinics={hasMultipleClinics}
              isTablet={isTablet}
            />
          )}
        </div>

        <div
          style={{
            flex: 1,
            padding: isMobile ? (isAgendaContext ? 6 : 10) : 18,
          }}
        >
        {showOnboardingCard && <OnboardingWelcomeCard />}
          <Outlet />
        </div>
      </main>

      {installPrompt && !installDismissed && !isStandalone ? (
        <InstallOdontoFlowPrompt
          isMobile={isMobile}
          onInstall={handleInstallApp}
          onDismiss={handleDismissInstall}
        />
      ) : null}
    </div>
  )
}

function MobileTopBar({
  user,
  logout,
  moduleMeta,
  activeClinicId,
  clinicOptions,
  loadingClinics,
  setActiveClinicId,
  activeClinicName,
  hasMultipleClinics,
  onOpenMenu,
  isVerySmallMobile,
  isAgendaContext = false,
}) {
  const visibleClinicName = activeClinicName || "Clínica"
  const showClinicSelector = hasMultipleClinics
  const moduleLabel = `${moduleMeta.group} / ${moduleMeta.title}`

  if (isAgendaContext) {
    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "auto minmax(0, 1fr) auto",
          gap: 7,
          alignItems: "center",
          width: "100%",
        }}
      >
        <button
          type="button"
          onClick={onOpenMenu}
          aria-label="Abrir menú"
          style={{
            width: 34,
            height: 34,
            borderRadius: 12,
            border: "1px solid #dbe4f0",
            background: "#ffffff",
            color: "#0f172a",
            display: "grid",
            placeItems: "center",
            fontSize: 18,
            fontWeight: 950,
            boxShadow: "0 6px 14px rgba(15,23,42,0.06)",
            cursor: "pointer",
          }}
        >
          ☰
        </button>

        {showClinicSelector ? (
          <select
            aria-label="Seleccionar clínica"
            value={activeClinicId || ""}
            onChange={(e) => setActiveClinicId(e.target.value)}
            style={{
              minHeight: 34,
              width: "100%",
              minWidth: 0,
              borderRadius: 12,
              border: "1px solid #dbe4f0",
              background: "#ffffff",
              color: "#0f172a",
              padding: "0 10px",
              fontSize: 11.5,
              fontWeight: 850,
              outline: "none",
              boxShadow: "0 6px 14px rgba(15,23,42,0.035)",
            }}
            disabled={loadingClinics}
          >
            {!activeClinicId && (
              <option value="" disabled>
                {loadingClinics ? "Cargando..." : "Clínica"}
              </option>
            )}

            {clinicOptions.map((clinic) => (
              <option key={clinic.id} value={clinic.id}>
                {clinic.name}
              </option>
            ))}
          </select>
        ) : (
          <div
            title={visibleClinicName}
            style={{
              minHeight: 34,
              minWidth: 0,
              padding: "0 11px",
              borderRadius: 12,
              border: "1px solid #dbe4f0",
              background: "#ffffff",
              color: "#0f172a",
              display: "flex",
              alignItems: "center",
              fontSize: 11.5,
              fontWeight: 900,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              boxShadow: "0 6px 14px rgba(15,23,42,0.035)",
            }}
          >
            {visibleClinicName}
          </div>
        )}

        <MobileAccountButton user={user} logout={logout} size={32} />
      </div>
    )
  }

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: showClinicSelector
          ? isVerySmallMobile
            ? "auto minmax(0, 1fr) auto"
            : "auto minmax(0, 1fr) minmax(118px, 34vw) auto"
          : "auto minmax(0, 1fr) auto",
        gap: 7,
        alignItems: "center",
        width: "100%",
      }}
    >
      <button
        type="button"
        onClick={onOpenMenu}
        aria-label="Abrir menú"
        style={{
          width: 38,
          height: 38,
          borderRadius: 13,
          border: "1px solid #dbe4f0",
          background: "#ffffff",
          color: "#0f172a",
          display: "grid",
          placeItems: "center",
          fontSize: 20,
          fontWeight: 950,
          boxShadow: "0 8px 18px rgba(15,23,42,0.07)",
          cursor: "pointer",
        }}
      >
        ☰
      </button>

      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 13.5,
            fontWeight: 950,
            color: "#0f172a",
            lineHeight: 1.05,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {moduleMeta.title}
        </div>

        <div
          style={{
            marginTop: 2,
            fontSize: 10.5,
            fontWeight: 850,
            color: "#64748b",
            lineHeight: 1.15,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {showClinicSelector ? moduleLabel : `${moduleMeta.group} · ${visibleClinicName}`}
        </div>
      </div>

      {showClinicSelector && !isVerySmallMobile ? (
        <select
          aria-label="Seleccionar clínica"
          value={activeClinicId || ""}
          onChange={(e) => setActiveClinicId(e.target.value)}
          style={{
            minHeight: 36,
            width: "100%",
            minWidth: 0,
            borderRadius: 13,
            border: "1px solid #dbe4f0",
            background: "#ffffff",
            color: "#0f172a",
            padding: "0 10px",
            fontSize: 11.5,
            fontWeight: 850,
            outline: "none",
            boxShadow: "0 6px 14px rgba(15,23,42,0.035)",
          }}
          disabled={loadingClinics}
        >
          {!activeClinicId && (
            <option value="" disabled>
              {loadingClinics ? "Cargando..." : "Clínica"}
            </option>
          )}

          {clinicOptions.map((clinic) => (
            <option key={clinic.id} value={clinic.id}>
              {clinic.name}
            </option>
          ))}
        </select>
      ) : null}

      {showClinicSelector && isVerySmallMobile ? null : (
        <MobileAccountButton user={user} logout={logout} size={32} />
      )}

      {showClinicSelector && isVerySmallMobile ? (
        <div
          style={{
            gridColumn: "1 / -1",
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) auto",
            gap: 7,
            alignItems: "center",
          }}
        >
          <select
            aria-label="Seleccionar clínica"
            value={activeClinicId || ""}
            onChange={(e) => setActiveClinicId(e.target.value)}
            style={{
              minHeight: 34,
              width: "100%",
              minWidth: 0,
              borderRadius: 12,
              border: "1px solid #dbe4f0",
              background: "#ffffff",
              color: "#0f172a",
              padding: "0 10px",
              fontSize: 11.5,
              fontWeight: 850,
              outline: "none",
            }}
            disabled={loadingClinics}
          >
            {!activeClinicId && (
              <option value="" disabled>
                {loadingClinics ? "Cargando..." : "Clínica"}
              </option>
            )}

            {clinicOptions.map((clinic) => (
              <option key={clinic.id} value={clinic.id}>
                {clinic.name}
              </option>
            ))}
          </select>

          <MobileAccountButton user={user} logout={logout} size={30} />
        </div>
      ) : null}
    </div>
  )
}

function MobileAccountButton({ user, logout, size = 32 }) {
  const [open, setOpen] = useState(false)

  const initial = String(user?.name || user?.email || "U").trim().charAt(0).toUpperCase()

  const handleLogout = () => {
    setOpen(false)
    logout?.()
  }

  return (
    <div style={{ position: "relative", display: "inline-grid", placeItems: "center" }}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-label="Abrir menú de usuario"
        title={user?.name || user?.email || "Usuario"}
        style={{
          width: size,
          height: size,
          borderRadius: 999,
          border: "none",
          background: "linear-gradient(135deg, #2563eb, #4f46e5)",
          color: "#ffffff",
          display: "grid",
          placeItems: "center",
          fontWeight: 950,
          fontSize: size <= 30 ? 11.5 : 12,
          boxShadow: "0 8px 16px rgba(37,99,235,0.20)",
          cursor: "pointer",
          padding: 0,
        }}
      >
        {initial}
      </button>

      {open ? (
        <div
          style={{
            position: "absolute",
            right: 0,
            top: size + 8,
            zIndex: 80,
            width: 218,
            padding: 10,
            borderRadius: 16,
            background: "#ffffff",
            border: "1px solid #e2e8f0",
            boxShadow: "0 22px 55px rgba(15,23,42,0.22)",
            display: "grid",
            gap: 8,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                color: "#0f172a",
                fontSize: 12.5,
                fontWeight: 950,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {user?.name || "Usuario"}
            </div>

            <div
              style={{
                marginTop: 2,
                color: "#64748b",
                fontSize: 11,
                fontWeight: 750,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {user?.email || "Sesión activa"}
            </div>
          </div>

          <button
            type="button"
            onClick={handleLogout}
            style={{
              minHeight: 34,
              borderRadius: 12,
              border: "1px solid #fecaca",
              background: "#fef2f2",
              color: "#991b1b",
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 950,
              width: "100%",
            }}
          >
            Cerrar sesión
          </button>
        </div>
      ) : null}
    </div>
  )
}

function DesktopTopBar({
  user,
  logout,
  moduleMeta,
  activeClinicId,
  clinicOptions,
  loadingClinics,
  setActiveClinicId,
  activeClinicName,
  hasMultipleClinics,
  isTablet,
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        flexWrap: "wrap",
        minHeight: 40,
      }}
    >
      <div
        style={{
          minWidth: 0,
          display: "grid",
          gap: 2,
        }}
      >
        <div
          style={{
            fontSize: 10.5,
            fontWeight: 900,
            textTransform: "uppercase",
            letterSpacing: 0.7,
            color: "#64748b",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {moduleMeta.group}
        </div>

        <div
          style={{
            fontSize: 16,
            fontWeight: 950,
            color: "#0f172a",
            lineHeight: 1.1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {moduleMeta.title}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 8,
          flexWrap: "wrap",
          maxWidth: "100%",
        }}
      >
        {hasMultipleClinics ? (
          <select
            id="global-clinic-selector"
            aria-label="Seleccionar clínica"
            value={activeClinicId || ""}
            onChange={(e) => setActiveClinicId(e.target.value)}
            style={{
              ...ui.select,
              minHeight: 38,
              width: isTablet ? "min(100%, 320px)" : 260,
              fontWeight: 850,
              paddingTop: 0,
              paddingBottom: 0,
            }}
            disabled={loadingClinics}
          >
            {!activeClinicId && (
              <option value="" disabled>
                {loadingClinics ? "Cargando clínicas..." : "Selecciona una clínica"}
              </option>
            )}

            {clinicOptions.map((clinic) => (
              <option key={clinic.id} value={clinic.id}>
                {clinic.name}
              </option>
            ))}
          </select>
        ) : (
          <div
            title={activeClinicName || "Clínica"}
            style={{
              minHeight: 38,
              maxWidth: 300,
              padding: "0 12px",
              borderRadius: 14,
              border: "1px solid #dbe4f0",
              background: "#ffffff",
              color: "#0f172a",
              display: "flex",
              alignItems: "center",
              gap: 8,
              boxShadow: "0 6px 16px rgba(15,23,42,0.035)",
              fontSize: 12.5,
              fontWeight: 850,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <span style={{ color: "#64748b", fontWeight: 900 }}>Clínica</span>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {activeClinicName || "Selecciona clínica"}
            </span>
          </div>
        )}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 9,
            padding: "5px 8px",
            borderRadius: 14,
            background: "#ffffff",
            border: "1px solid #e2e8f0",
            boxShadow: "0 6px 16px rgba(15, 23, 42, 0.045)",
            minHeight: 38,
          }}
        >
          <div
            title={user?.name || "Usuario"}
            style={{
              width: 28,
              height: 28,
              borderRadius: 999,
              background: "linear-gradient(135deg, #2563eb, #4f46e5)",
              color: "#ffffff",
              display: "grid",
              placeItems: "center",
              fontWeight: 950,
              fontSize: 11.5,
              flex: "0 0 auto",
            }}
          >
            {String(user?.name || "U").trim().charAt(0).toUpperCase()}
          </div>

          <button
            onClick={logout}
            style={{
              border: "none",
              background: "transparent",
              color: "#334155",
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 900,
              padding: "0 4px",
            }}
          >
            Salir
          </button>
        </div>
      </div>
    </div>
  )
}

function MobileNavigationDrawer({
  open,
  onClose,
  user,
  logout,
  subscriptionLabel,
  subscriptionMeta,
  subscriptionTone,
}) {
  if (!open) return null

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 999999,
        background: "rgba(15,23,42,0.48)",
        backdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "stretch",
      }}
      onMouseDown={onClose}
    >
      <div
        style={{
          width: "min(286px, 86vw)",
          height: "100%",
          background: "#0b1220",
          boxShadow: "24px 0 70px rgba(15,23,42,0.35)",
          display: "flex",
          flexDirection: "column",
          animation: "ofMobileMenuIn 160ms ease-out",
        }}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div
          style={{
            minHeight: 50,
            padding: "9px 12px",
            borderBottom: "1px solid rgba(148,163,184,0.18)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
          }}
        >
          <div>
            <div
              style={{
                color: "#ffffff",
                fontSize: 14,
                fontWeight: 950,
                letterSpacing: "-0.03em",
              }}
            >
              Menú
            </div>
            <div
              style={{
                color: "rgba(226,232,240,0.68)",
                fontSize: 11,
                fontWeight: 700,
                marginTop: 2,
              }}
            >
              Navegación principal
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar menú"
            style={{
              width: 34,
              height: 34,
              borderRadius: 12,
              border: "1px solid rgba(226,232,240,0.22)",
              background: "rgba(255,255,255,0.08)",
              color: "#ffffff",
              fontSize: 18,
              fontWeight: 900,
              cursor: "pointer",
              display: "grid",
              placeItems: "center",
            }}
          >
            ×
          </button>
        </div>

        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <Sidebar
            user={user}
            logout={logout}
            subscriptionLabel={subscriptionLabel}
            subscriptionMeta={subscriptionMeta}
            subscriptionTone={subscriptionTone}
          />
        </div>
      </div>

      <style>
        {`
          @keyframes ofMobileMenuIn {
            from {
              transform: translateX(-18px);
              opacity: 0.72;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `}
      </style>
    </div>
  )
}

function InstallOdontoFlowPrompt({ isMobile, onInstall, onDismiss }) {
  return (
    <div
      style={{
        position: "fixed",
        right: isMobile ? 12 : 18,
        bottom: isMobile ? 12 : 18,
        zIndex: 80,
        width: isMobile ? "calc(100vw - 24px)" : 340,
        maxWidth: "calc(100vw - 24px)",
        padding: 12,
        borderRadius: 20,
        background: "rgba(255,255,255,0.96)",
        border: "1px solid #dbe4f0",
        boxShadow: "0 24px 60px rgba(15,23,42,0.18)",
        backdropFilter: "blur(14px)",
        display: "grid",
        gap: 10,
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "auto minmax(0, 1fr) auto",
          gap: 10,
          alignItems: "center",
        }}
      >
        <OdontoFlowLogo
          variant="icon"
          theme="dark"
          size="md"
          showText={false}
          appIcon
        />

        <div style={{ minWidth: 0 }}>
          <div
            style={{
              color: "#0f172a",
              fontSize: 13,
              fontWeight: 950,
              letterSpacing: "-0.02em",
              lineHeight: 1.15,
            }}
          >
            Instalar OdontoFlow
          </div>

          <div
            style={{
              color: "#64748b",
              fontSize: 11.5,
              fontWeight: 750,
              marginTop: 2,
              lineHeight: 1.3,
            }}
          >
            Accede más rápido desde tu dispositivo.
          </div>
        </div>

        <button
          type="button"
          onClick={onDismiss}
          aria-label="Ocultar instalación"
          style={{
            width: 30,
            height: 30,
            borderRadius: 10,
            border: "1px solid #e2e8f0",
            background: "#ffffff",
            color: "#64748b",
            cursor: "pointer",
            fontSize: 16,
            fontWeight: 950,
            display: "grid",
            placeItems: "center",
          }}
        >
          ×
        </button>
      </div>

      <button
        type="button"
        onClick={onInstall}
        style={{
          ...ui.primaryButton,
          width: "100%",
          justifyContent: "center",
          minHeight: 40,
        }}
      >
        Instalar aplicación
      </button>
    </div>
  )
}

export default function AppLayout() {
  return (
    <ClinicProvider>
      <AppShell />
    </ClinicProvider>
  )
}